import './styles/main.css';
import 'leaflet/dist/leaflet.css';

import { initAuth, checkSession, showLoginPage, showAppView } from './modules/auth.ts';
import { initDashboard } from './modules/dashboard.ts';
import { initEditor, formatDoc, selectChapterById } from './modules/editor.ts';
import { initCronograma } from './modules/cronograma.ts';
import { initGlitch } from './modules/glitch.ts';
import { initAtlas, updateAtlasMarkers, refreshMapLayout } from './modules/atlas.ts';
import { initTeoria } from './modules/teoria.ts';
import { initCasos } from './modules/casos.ts';
import { initImagenes } from './modules/imagenes.ts';
import { initEnlaces } from './modules/enlaces.ts';
import { initBackup } from './modules/backup.ts';
import type { Tarea, GlitchIGU } from './types/index.ts';

// Exponer formatDoc globalmente para los botones del toolbar
(window as any).formatDoc = formatDoc;

function initNavigation() {
  const navButtons = document.querySelectorAll<HTMLButtonElement>('.nav-btn');
  const modules = document.querySelectorAll<HTMLElement>('.module-view');
  const viewTitle = document.getElementById('view-title');
  const viewSubtitle = document.getElementById('view-subtitle');

  const subtitles: Record<string, string> = {
    'mod-dashboard': 'Marco epistemológico, problema, hipótesis central y objetivos específicos (OE1, OE2, OE3)',
    'mod-redaccion': 'Redacción capitular continua persistida en SQLite con exportación a Word y PDF',
    'mod-analisis': 'Estratigrafía y morfología comparada (Panerai, Prinz, Secchi) - OE1',
    'mod-glitch': 'Cálculo de criticidad espacial IGU = (m + f + s) / (1 + r) y catalogación de anomalías (OE2)',
    'mod-tacticas': 'Repertorios de resistencia táctica y resignificación del hábitat (OE3)',
    'mod-bibliografia': 'Genealogía del Glitch, vacíos de frontera y taxonomía teórica en APA 7',
    'mod-tareas': 'Planificación y entregables del programa doctoral (Universidad de Zaragoza)',
    'mod-mapa': 'Cartografía espacial comparativa de El Raval, Barrio Yungay y Gamcheon Culture Village',
    'mod-imagenes': 'Catálogo visual de transectos, croquis fenomenológicos y planimetría histórica',
    'mod-enlaces': 'Geoportales municipales, visores cartográficos y fuentes documentales primarias'
  };

  const moduleToSlug: Record<string, string> = {
    'mod-dashboard': 'encuadre',
    'mod-redaccion': 'redaccion',
    'mod-analisis': 'analisis',
    'mod-glitch': 'glitches',
    'mod-tacticas': 'tacticas',
    'mod-bibliografia': 'bibliografia',
    'mod-tareas': 'cronograma',
    'mod-mapa': 'mapa',
    'mod-imagenes': 'imagenes',
    'mod-enlaces': 'geoportales'
  };

  const slugToModule: Record<string, string> = {
    'encuadre': 'mod-dashboard',
    'dashboard': 'mod-dashboard',
    'inicio': 'mod-dashboard',
    'redaccion': 'mod-redaccion',
    'manuscrito': 'mod-redaccion',
    'capitulos': 'mod-redaccion',
    'analisis': 'mod-analisis',
    'analisis-morfologico': 'mod-analisis',
    'morfologia': 'mod-analisis',
    'casos': 'mod-analisis',
    'glitch': 'mod-glitch',
    'glitches': 'mod-glitch',
    'igu': 'mod-glitch',
    'tacticas': 'mod-tacticas',
    'resistencia': 'mod-tacticas',
    'bibliografia': 'mod-bibliografia',
    'teoria': 'mod-bibliografia',
    'citas': 'mod-bibliografia',
    'apa': 'mod-bibliografia',
    'tareas': 'mod-tareas',
    'cronograma': 'mod-tareas',
    'hitos': 'mod-tareas',
    'mapa': 'mod-mapa',
    'atlas': 'mod-mapa',
    'cartografia': 'mod-mapa',
    'imagenes': 'mod-imagenes',
    'planos': 'mod-imagenes',
    'laminas': 'mod-imagenes',
    'archivo-visual': 'mod-imagenes',
    'enlaces': 'mod-enlaces',
    'geoportales': 'mod-enlaces',
    'fuentes': 'mod-enlaces'
  };

  function switchModule(targetId: string, options: { updateHash?: boolean; chapterId?: string } = {}) {
    const { updateHash = true, chapterId } = options;
    const btn = Array.from(navButtons).find(b => b.getAttribute('data-target') === targetId);
    if (!btn) return;

    navButtons.forEach(b => b.classList.remove('active'));
    modules.forEach(m => m.classList.remove('active'));

    btn.classList.add('active');
    const targetSection = document.getElementById(targetId);
    targetSection?.classList.add('active');

    if (viewTitle) {
      const customTitle = btn.getAttribute('data-title');
      const labelEl = btn.querySelector('.nav-label');
      viewTitle.textContent = customTitle || labelEl?.textContent || btn.textContent?.trim() || '';
    }
    if (viewSubtitle && subtitles[targetId]) {
      viewSubtitle.innerText = subtitles[targetId];
    }

    if (targetId === 'mod-mapa') {
      refreshMapLayout();
    }

    // Persistir módulo activo en localStorage
    localStorage.setItem('doctoral_active_module', targetId);

    // Si es redacción y se especificó un capítulo, seleccionarlo
    if (targetId === 'mod-redaccion') {
      const activeCap = chapterId || localStorage.getItem('doctoral_active_chapter_id');
      if (activeCap) {
        selectChapterById(activeCap);
      }
    }

    // Sincronizar URL hash
    if (updateHash) {
      const slug = moduleToSlug[targetId] || targetId.replace('mod-', '');
      let newHash = `#${slug}`;
      if (targetId === 'mod-redaccion') {
        const curCap = chapterId || (document.getElementById('chapter-select') as HTMLSelectElement)?.value || localStorage.getItem('doctoral_active_chapter_id');
        if (curCap) {
          newHash = `#redaccion/cap-${curCap}`;
        }
      }
      if (window.location.hash !== newHash) {
        window.history.replaceState(null, '', newHash);
      }
    }
  }

  function parseRouteFromHash(): { moduleId: string; chapterId?: string } {
    const rawHash = window.location.hash.replace(/^#\/?/, '').trim();
    if (!rawHash) {
      const savedModule = localStorage.getItem('doctoral_active_module');
      const savedChapter = localStorage.getItem('doctoral_active_chapter_id') || undefined;
      return {
        moduleId: savedModule && document.getElementById(savedModule) ? savedModule : 'mod-dashboard',
        chapterId: savedChapter
      };
    }

    const segments = rawHash.split(/[/?&]/);
    const primary = segments[0].toLowerCase();
    const moduleId = slugToModule[primary] || (document.getElementById(primary) ? primary : (document.getElementById(`mod-${primary}`) ? `mod-${primary}` : 'mod-dashboard'));

    let chapterId: string | undefined;
    const capMatch = rawHash.match(/(?:cap[-=]|capitulo[-=]|\/|\?cap=)(\d+)/i);
    if (capMatch && capMatch[1]) {
      chapterId = capMatch[1];
    } else if (segments[1] && /^\d+$/.test(segments[1])) {
      chapterId = segments[1];
    }

    return { moduleId, chapterId };
  }

  // Evento click en botones de navegación
  navButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const targetId = btn.getAttribute('data-target');
      if (targetId) {
        switchModule(targetId, { updateHash: true });
      }
    });
  });

  // Escuchar navegación del historial (Atrás / Adelante en navegador)
  window.addEventListener('hashchange', () => {
    const route = parseRouteFromHash();
    switchModule(route.moduleId, { updateHash: false, chapterId: route.chapterId });
  });

  // Ruta inicial al cargar / recargar la página
  const initialRoute = parseRouteFromHash();
  switchModule(initialRoute.moduleId, { updateHash: true, chapterId: initialRoute.chapterId });
}

function initSidebarToggle() {
  const toggleBtn = document.getElementById('btn-toggle-sidebar');
  const collapseBtn = document.getElementById('btn-collapse-sidebar');

  function setSidebarState(collapsed: boolean) {
    if (collapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    localStorage.setItem('sidebar_collapsed', collapsed ? 'true' : 'false');
    setTimeout(() => {
      refreshMapLayout();
    }, 320);
  }

  // Cargar estado guardado
  const isSavedCollapsed = localStorage.getItem('sidebar_collapsed') === 'true';
  if (isSavedCollapsed) {
    document.body.classList.add('sidebar-collapsed');
  }

  toggleBtn?.addEventListener('click', () => {
    const isNowCollapsed = !document.body.classList.contains('sidebar-collapsed');
    setSidebarState(isNowCollapsed);
  });

  collapseBtn?.addEventListener('click', () => {
    setSidebarState(true);
  });

  // Atajo de teclado: Ctrl+B o Alt+S
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey && e.key.toLowerCase() === 'b') || (e.altKey && e.key.toLowerCase() === 's')) {
      const activeEl = document.activeElement;
      const isInput = activeEl?.tagName === 'INPUT' || activeEl?.tagName === 'TEXTAREA' || activeEl?.getAttribute('contenteditable') === 'true';
      if (!isInput || (e.altKey && e.key.toLowerCase() === 's')) {
        e.preventDefault();
        const isNowCollapsed = !document.body.classList.contains('sidebar-collapsed');
        setSidebarState(isNowCollapsed);
      }
    }
  });
}

function updateTaskMetrics(tareas: Tarea[]) {
  const pillTasks = document.getElementById('pill-tasks-count');
  if (pillTasks) pillTasks.innerText = `Hitos: ${tareas.length}`;

  const completed = tareas.filter(t => t.estado === 'Completado' || (t as any).estado === 'completado').length;
  const inProgress = tareas.filter(t => t.estado === 'En progreso' || (t as any).estado === 'progreso').length;
  const total = tareas.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const metricPct = document.getElementById('metric-progress-pct');
  const metricDetail = document.getElementById('metric-progress-detail');
  const metricActive = document.getElementById('metric-active-tasks');

  if (metricPct) metricPct.innerText = `${pct}%`;
  if (metricDetail) metricDetail.innerText = `${completed} de ${total} hitos oficiales completados`;
  if (metricActive) metricActive.innerText = inProgress.toString();
}

function updateGlitchMetrics(glitches: GlitchIGU[]) {
  const pillGlitch = document.getElementById('pill-glitch-count');
  if (pillGlitch) pillGlitch.innerText = `Glitches: ${glitches.length}`;

  const metricGlitches = document.getElementById('metric-total-glitches');
  if (metricGlitches) metricGlitches.innerText = glitches.length.toString();

  updateAtlasMarkers(glitches);
}

function updateWordStats(totalWords: number) {
  const pillWords = document.getElementById('pill-words-count');
  if (pillWords) pillWords.innerText = `Palabras: ${totalWords.toLocaleString()}`;

  const metricWords = document.getElementById('metric-total-words');
  if (metricWords) metricWords.innerText = totalWords.toLocaleString();
}

let appModulesInitialized = false;

function startDoctoralApplication() {
  if (appModulesInitialized) return;
  appModulesInitialized = true;

  initNavigation();
  initAtlas();
  initDashboard();

  initEditor((totalWords) => {
    updateWordStats(totalWords);
  });

  initCronograma((tareas) => {
    updateTaskMetrics(tareas);
  });

  initGlitch((glitches) => {
    updateGlitchMetrics(glitches);
  });

  initTeoria();
  initCasos();
  initImagenes();
  initEnlaces();

  initBackup(() => {
    // Recargar módulos cuando se restaure un backup
    window.location.reload();
  });
}

// Inicialización de la aplicación
window.addEventListener('DOMContentLoaded', async () => {
  initSidebarToggle();

  // Configurar escuchadores del sistema de autenticación
  initAuth(
    (user) => {
      console.log('Autenticación doctoral verificada en SQLite:', user.username);
      startDoctoralApplication();
    },
    () => {
      // Al cerrar sesión, recargar para limpiar el DOM de datos sensibles
      window.location.reload();
    }
  );

  // Verificar sesión existente con token en SQLite
  const user = await checkSession();
  if (user) {
    showAppView(user, () => {
      window.location.reload();
    });
    startDoctoralApplication();
  } else {
    showLoginPage();
  }
});
